import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Shield, ChevronLeft } from "lucide-react";

export default function Privacy() {
  const updated = "9 September 2026";
  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white">
      <SEO title="Privacy Policy · Nexus" description="How Nexus collects, uses and protects your personal data." path="/privacy" />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-zinc-400 hover:text-white mb-8"><ChevronLeft className="h-3 w-3" /> Back</Link>
        <div className="flex items-center gap-3 mb-4">
          <Shield className="h-6 w-6 text-nexus-blue" />
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Privacy Policy</div>
        </div>
        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter">Your data, on your terms.</h1>
        <p className="text-zinc-500 mt-2 text-sm">Last updated {updated}</p>

        <div className="prose prose-invert prose-zinc max-w-none mt-10 space-y-6 text-zinc-300 text-sm leading-relaxed">
          <section>
            <h2 className="font-display font-bold text-xl text-white mt-0">1. Who we are</h2>
            <p>Nexus (&ldquo;we&rdquo;, &ldquo;our&rdquo;) is operated by CollabNexus Ltd, a private limited company registered in England &amp; Wales (Company No. 17438481), registered office 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ. We connect UK-based social media creators with brands for paid marketing collaborations. Contact us at <a href="mailto:info@collabnexus.co.uk" className="text-nexus-blue hover:text-white">info@collabnexus.co.uk</a>.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">2. What we collect</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Account data</strong> — name, email, password hash, role (brand or creator), company name.</li>
              <li><strong>Profile data</strong> — creator niches, platforms, follower count, portfolio, bio, avatar.</li>
              <li><strong>Social platform data</strong> — when you connect Instagram, TikTok, or YouTube via OAuth, we receive: profile ID, username, follower count, and (with your consent) recent media metadata used only for creator matching and the Growth AI audit. We do NOT post to your accounts.</li>
              <li><strong>Financial data</strong> — Stripe processes all payments. We store transaction records, hire amounts, and payout balances. We never see your card details.</li>
              <li><strong>Usage data</strong> — application IPs, browser type, page views (via analytics).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">3. Why we collect it</h2>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>To match creators with relevant brand campaigns.</li>
              <li>To process escrow payments and release payouts.</li>
              <li>To run the Growth AI creator audit that suggests posting strategy.</li>
              <li>To prevent fraud and abuse.</li>
              <li>To send transactional emails (never marketing without opt-in).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">4. Who we share it with</h2>
            <p>Only the third parties strictly needed to run the service:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong>Stripe</strong> — payments.</li>
              <li><strong>Anthropic Claude &amp; Google Gemini</strong> — AI features (brief drafting, Growth AI audit, case studies). No raw PII is sent — only campaign text and public profile data.</li>
              <li><strong>Meta, TikTok, Google</strong> — only during OAuth to confirm your identity and pull public metrics you have authorised.</li>
              <li><strong>Hosting / infra</strong> — Emergent-managed AWS-region servers in the EU/UK.</li>
            </ul>
            <p>We never sell your data.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">5. Your rights (UK GDPR)</h2>
            <p>You can at any time:</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Request a copy of all data we hold (email us).</li>
              <li>Correct anything inaccurate from your Settings page.</li>
              <li>Delete your account and all associated data via <Link to="/data-deletion" className="text-nexus-blue hover:text-white">our Data Deletion page</Link>.</li>
              <li>Object to processing or withdraw consent.</li>
              <li>Complain to the UK Information Commissioner&apos;s Office.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">6. How long we keep it</h2>
            <p>Account data: while your account is active + 30 days after deletion request (in case you change your mind). Financial records: 7 years (UK tax law). Analytics: 26 months. Social OAuth tokens: rotated every 60 days; deleted on disconnect.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">7. Cookies</h2>
            <p>We use only strictly necessary cookies for authentication (session token) and analytics. No third-party advertising cookies. No cross-site tracking.</p>
          </section>

          <section data-testid="privacy-social-login">
            <h2 className="font-display font-bold text-xl text-white">7a. Social login data (Instagram, TikTok, Google/YouTube)</h2>
            <p>If you sign in or connect an account with Instagram, TikTok or Google, we receive only the data you consent to on the provider&apos;s screen: your public profile (name, handle, avatar), email address, and public audience statistics (follower/subscriber count, view count, video count) and, where granted, a list of your recent public posts. For Google we use the read-only <code>youtube.readonly</code> scope to read your own channel statistics; we never read private videos, analytics or comments and never write to your channel.</p>
            <p>This data is used solely to populate your Nexus creator profile so brands can verify your reach. It is stored encrypted at rest, refreshed weekly, shown only on your own profile, never combined with data from other providers, and never sold, transferred or used for advertising. You can revoke access at any time in Settings → Connections (or at <a href="https://myaccount.google.com/permissions" className="text-nexus-blue hover:text-white" target="_blank" rel="noreferrer">myaccount.google.com/permissions</a>), which deletes the token and cached data immediately. Deleting your Nexus account removes everything else within 30 days.</p>
            <p>Nexus&apos;s use and transfer of information received from Google APIs adheres to the <a href="https://developers.google.com/terms/api-services-user-data-policy" className="text-nexus-blue hover:text-white" target="_blank" rel="noreferrer">Google API Services User Data Policy</a>, including the Limited Use requirements.</p>
          </section>


          <section>
            <h2 className="font-display font-bold text-xl text-white">8. Changes</h2>
            <p>We&apos;ll email you at least 14 days before any material change. Continued use after that constitutes acceptance.</p>
          </section>

          <section>
            <h2 className="font-display font-bold text-xl text-white">9. Contact</h2>
            <p>Questions? <a href="mailto:info@collabnexus.co.uk" className="text-nexus-blue hover:text-white">info@collabnexus.co.uk</a></p>
          </section>
        </div>
      </div>
    </div>
  );
}
